/**
 * Library — replaces the old DraftLibraryPage.
 *
 * Shows the user's past AI-generated contracts (drafts + reviews) in
 * a single filterable list. Data source: GET /api/draft-library/* (legacy
 * route name, surfaced as "Library" in the UI).
 *
 * The file keeps the `DraftLibraryPage` export so existing routes work.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Download,
  FileText,
  Search,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { safeFetch } from '@/lib/safeFetch';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API = getBackendBaseUrl();

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
};

const DraftLibraryPage = () => {
  const { session } = useAuth();
  const token = session?.access_token;
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [action, setAction] = useState('');
  const [period, setPeriod] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/draft-library/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setItems(Array.isArray(data?.drafts) ? data.drafts : (Array.isArray(data) ? data : []));
    } catch { /* non-fatal */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Build distinct contract-type list.
  const contractTypes = useMemo(() => {
    const s = new Set();
    items.forEach((i) => { if (i.contract_type) s.add(i.contract_type); });
    return Array.from(s).sort();
  }, [items]);

  // Filter pipeline
  const filtered = useMemo(() => items.filter((i) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = ((i.title || '') + ' ' + (i.filename || '')).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (type && i.contract_type !== type) return false;
    if (action) {
      const act = (i.action || '').toLowerCase();
      if (act !== action.toLowerCase()) return false;
    }
    if (period) {
      const ts = new Date(i.created_at || 0).getTime();
      const now = Date.now();
      const day = 86400000;
      let cutoff = 0;
      if (period === 'Today') cutoff = now - day;
      else if (period === 'This week') cutoff = now - 7 * day;
      else if (period === 'This month') cutoff = now - 30 * day;
      if (cutoff && ts < cutoff) return false;
    }
    return true;
  }), [items, search, type, action, period]);

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="library-page"
      className="min-h-screen bg-[#FAFAF8]"
    >
      <div className="max-w-[1180px] mx-auto px-6 py-8">
        <header className="mb-6">
          <h1
            className="text-[28px] font-semibold text-[#1B1B1A]"
            data-testid="library-title"
          >
            {isAr ? 'المكتبة' : 'Library'}
          </h1>
          <p
            className="text-[13.5px] text-[#666] mt-1"
            data-testid="library-subtitle"
          >
            {isAr ? 'العقود والمراجعات المُولَّدة بالذكاء الاصطناعي' : 'Your AI-generated contracts and reviews'}
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="library-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="library-search"
              placeholder={isAr ? 'ابحث...' : 'Search...'}
              className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E5E1D8] bg-white text-[13px] placeholder:text-[#999] focus:outline-none focus:border-[#1B1B1A]"
            />
          </div>
          <FilterDropdown label="Type" value={type} options={contractTypes} onChange={setType} testId="library-filter-type" />
          <FilterDropdown label="Action" value={action} options={['Drafted', 'Reviewed']} onChange={setAction} testId="library-filter-action" />
          <FilterDropdown label="Date" value={period} options={['Today', 'This week', 'This month', 'All time']} onChange={(v) => setPeriod(v === 'All time' ? '' : v)} testId="library-filter-date" />
        </div>

        {filtered.length === 0 ? (
          <LibraryEmptyState isAr={isAr} onCTA={() => navigate('/chat')} />
        ) : (
          <ul className="space-y-2" data-testid="library-list">
            {filtered.map((c) => (
              <ContractEntry key={c.id || c._id} contract={c} token={token} isAr={isAr} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

function FilterDropdown({ label, value, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border border-[#E5E1D8] bg-white text-[12.5px] text-[#1B1B1A] px-3 py-1.5 hover:border-[#C8BFA8]"
      >
        {label}{value ? `: ${value}` : ''} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border border-[#E5E1D8] bg-white shadow-md py-1 text-[13px]">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="w-full text-left px-3 py-1.5 text-[#1B1B1A] hover:bg-[#F3EFE6]"
          >
            All {label.toLowerCase()}s
          </button>
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange(o); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-[#1B1B1A] hover:bg-[#F3EFE6]"
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractEntry({ contract, token, isAr }) {
  const navigate = useNavigate();
  const [downloadOpen, setDownloadOpen] = useState(false);

  const title = contract.title || contract.filename || `Contract ${contract.id?.slice(-6) || ''}`;
  const ct = contract.contract_type || (isAr ? 'عام' : 'General');
  const act = contract.action || 'Drafted';
  const risks = Number(contract.risk_count || 0);
  const compliance = contract.compliance_status || null;
  const templates = contract.templates_used || [];

  const downloadAs = async (fmt) => {
    setDownloadOpen(false);
    try {
      // safeFetch — same rationale as useWorkflows.renderDocument:
      // the platform fetch-patcher drains 4xx bodies before we get a
      // chance to call .blob(), surfacing "body stream already read".
      const r = await safeFetch(`${API}/api/draft-library/${contract.id}/download?fmt=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* no-op */ }
  };

  return (
    <li
      data-testid={`library-entry-${contract.id || ''}`}
      className="rounded-lg border border-[#E5E1D8] bg-white p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4 hover:border-[#C8BFA8] transition"
    >
      <div className="flex-1 min-w-0">
        <h3 className="text-[14.5px] font-medium text-[#1B1B1A] truncate">
          <FileText className="inline w-4 h-4 mr-1 text-[#888]" />
          {title}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px]">
          <Tag>{ct}</Tag>
          <Tag>{act}</Tag>
          {contract.creator_name && (
            <Tag data-testid="library-card-creator">{isAr ? `بواسطة ${contract.creator_name}` : `By ${contract.creator_name}`}</Tag>
          )}
          <span className="text-[#888]">{formatDate(contract.created_at)}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-[#666]">
          {contract.article_count > 0 && <span>{contract.article_count} articles</span>}
          {contract.word_count > 0 && <span>{Number(contract.word_count).toLocaleString()} words</span>}
          {risks > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertTriangle className="w-3 h-3" /> {risks} risks
            </span>
          )}
          {compliance && (
            <span className={'inline-flex items-center gap-1 ' + (compliance === 'compliant' ? 'text-emerald-700' : 'text-amber-700')}>
              <CheckCircle className="w-3 h-3" /> {compliance}
            </span>
          )}
        </div>
        {templates.length > 0 && (
          <p className="mt-2 text-[11.5px] text-[#888]">
            {isAr ? 'النماذج المستخدمة' : 'Templates used'}: {templates.join(', ')}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/chat?draft=${contract.id}`)}
          data-testid="library-entry-open"
          className="inline-flex items-center justify-center rounded-full bg-[#1B1B1A] text-white text-[12.5px] px-3.5 py-1.5 hover:bg-black/85"
        >
          {isAr ? 'فتح' : 'Open'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => setDownloadOpen((v) => !v)}
            data-testid="library-entry-download"
            className="inline-flex items-center gap-1 rounded-full border border-[#E5E1D8] bg-white text-[12.5px] text-[#1B1B1A] px-3 py-1.5 hover:border-[#C8BFA8]"
          >
            <Download className="w-3.5 h-3.5" /> {isAr ? 'تنزيل' : 'Download'} <ChevronDown className="w-3 h-3" />
          </button>
          {downloadOpen && (
            <div className="absolute right-0 z-20 mt-1 w-32 rounded-md border border-[#E5E1D8] bg-white shadow-md py-1 text-[13px]">
              <button type="button" onClick={() => downloadAs('docx')} className="w-full text-left px-3 py-1.5 hover:bg-[#F3EFE6]">DOCX</button>
              <button type="button" onClick={() => downloadAs('pdf')} className="w-full text-left px-3 py-1.5 hover:bg-[#F3EFE6]">PDF</button>
              <button type="button" onClick={() => downloadAs('txt')} className="w-full text-left px-3 py-1.5 hover:bg-[#F3EFE6]">Text</button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/chat?review=${contract.id}`)}
          className="inline-flex items-center rounded-full border border-[#E5E1D8] bg-white text-[12.5px] text-[#1B1B1A] px-3 py-1.5 hover:border-[#C8BFA8]"
        >
          {isAr ? 'مراجعة مجدداً' : 'Review again'}
        </button>
      </div>
    </li>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#F3EFE6] text-[#1B1B1A] text-[11px] px-2 py-0.5">
      {children}
    </span>
  );
}

function LibraryEmptyState({ isAr, onCTA }) {
  return (
    <div
      data-testid="library-empty"
      className="rounded-lg border border-dashed border-[#D5CCB8] bg-white p-12 text-center"
    >
      <FileText className="w-9 h-9 text-[#B8AC8E] mx-auto mb-4" />
      <h3 className="text-[16px] font-medium text-[#1B1B1A] mb-2">
        {isAr ? 'لا توجد عقود بعد' : 'No contracts yet'}
      </h3>
      <p className="text-[13px] text-[#666] mb-1 max-w-md mx-auto">
        {isAr
          ? 'العقود المُصاغة والمُراجَعة ستظهر هنا.'
          : 'Drafted and reviewed contracts will appear here.'}
      </p>
      <p className="text-[13px] text-[#666] mb-5 max-w-md mx-auto">
        {isAr
          ? 'ابدأ بطلب صياغة أو مراجعة عقد من المساعد الذكي.'
          : 'Start by asking Bayenah to draft or review a contract in the Smart Assistant.'}
      </p>
      <button
        type="button"
        onClick={onCTA}
        data-testid="library-empty-cta"
        className="inline-flex items-center gap-1 rounded-full bg-[#1B1B1A] text-white text-[13px] px-4 py-2 hover:bg-black/85"
      >
        {isAr ? 'اذهب إلى المساعد الذكي' : 'Go to Smart Assistant'} →
      </button>
    </div>
  );
}

export default DraftLibraryPage;
